"use client";

import { Callout, Flex, Text, VisuallyHidden } from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";
import { Form } from "radix-ui";
import * as React from "react";
import { type ReactNode } from "react";
import {
  getMeQueryKey,
  Me,
  useAuthenticationInformation,
  useSendEmailChange,
  useVerifyEmailChange,
} from "../api/endpoint.js";
import { useElevatedAccessToken } from "../api/api-provider.js";
import { Button, Dialog, Label, TextField } from "./elements.js";
import { ElevatedAccess } from "./elevated-access.js";
import { Translation } from "./i18n/translation.js";
import { useTranslation } from "./i18n/use-translation.js";
import * as Otp from "./otp-input.js";
import { SaveButton } from "./save-button.js";
import { SetPasswordForm } from "./set-password-dialog.js";

interface ChangeEmailDialogProps {
  user: Me;
  children?: ReactNode;
}

export function ChangeEmailDialog({ children, user }: ChangeEmailDialogProps) {
  const [open, setOpen] = React.useState(false);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger>{children}</Dialog.Trigger>

      <Dialog.Content maxWidth="480px">
        <ElevatedAccess>
          <Content user={user} onClose={handleClose} />
        </ElevatedAccess>
      </Dialog.Content>
    </Dialog.Root>
  );
}

interface ContentProps {
  user: Me;
  onClose: () => void;
}

type Step =
  | { name: "send" }
  | { name: "verify"; newEmail: string }
  | { name: "set-password"; newEmail: string };

function Content({ user, onClose }: ContentProps) {
  const [step, setStep] = React.useState<Step>({ name: "send" });
  const authInfo = useAuthenticationInformation();

  const passwordIsSetUp =
    authInfo.data?.data.verificationMethods.Password?.isSetUp;

  const handleVerified = React.useCallback(
    (newEmail: string) => {
      if (passwordIsSetUp === false) {
        setStep({ name: "set-password", newEmail });
      } else {
        onClose();
      }
    },
    [passwordIsSetUp, onClose],
  );

  switch (step.name) {
    case "send":
      return (
        <SendEmailChangeForm
          user={user}
          onSuccess={(newEmail) => setStep({ name: "verify", newEmail })}
        />
      );
    case "verify":
      return (
        <VerifyEmailChangeForm
          newEmail={step.newEmail}
          onBack={() => setStep({ name: "send" })}
          onSuccess={() => handleVerified(step.newEmail)}
        />
      );
    case "set-password":
      return <SetPasswordStep newEmail={step.newEmail} onDone={onClose} />;
  }
}

interface SendEmailChangeFormProps {
  user: Me;
  onSuccess: (newEmail: string) => void;
}

function SendEmailChangeForm({ user, onSuccess }: SendEmailChangeFormProps) {
  const translate = useTranslation();
  const [newEmail, setNewEmail] = React.useState("");
  const sendEmailChange = useSendEmailChange({
    mutation: {
      onSuccess: (_response, variables) => {
        onSuccess(variables.data.newEmail);
      },
    },
  });

  return (
    <>
      <Dialog.Title mb="2">
        <Translation
          defaultMessage="Change email address"
          id="KdUdX9"
          description="Dialog title for changing the user's email address"
        />
      </Dialog.Title>

      <Dialog.Description mb="5">
        <Translation
          defaultMessage="We'll send a verification code to your new email address to confirm the change. Your current email is {email}."
          id="K6x8v6"
          description="Description explaining that a verification code will be sent to the new email, including the user's current email"
          values={{
            email: (
              <Text weight="bold" highContrast>
                {user.email}
              </Text>
            ),
          }}
        />
      </Dialog.Description>

      {sendEmailChange.error ? (
        <Callout.Root color="red" mt="-2" mb="0">
          <Callout.Text>
            {getMutationErrorMessage(sendEmailChange.error)}
          </Callout.Text>
        </Callout.Root>
      ) : null}

      <Form.Root
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = newEmail.trim();
          if (!trimmed) return;
          sendEmailChange.mutate({ data: { newEmail: trimmed } });
        }}
      >
        <Flex my="5" direction="column" gap="4">
          <Form.Field name="newEmail" asChild>
            <Flex direction="column" gap="1">
              <Form.Label asChild>
                <Label>
                  <Translation
                    defaultMessage="New email address"
                    id="dbgchn"
                    description="Label for the new email input"
                  />
                </Label>
              </Form.Label>
              <Form.Control asChild>
                <TextField
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={newEmail}
                  onChange={(event) => setNewEmail(event.target.value)}
                  placeholder={translate({
                    defaultMessage: "name@example.com",
                    id: "QyYabV",
                    description: "Placeholder for the new email input",
                  })}
                  disabled={sendEmailChange.isPending}
                />
              </Form.Control>
            </Flex>
          </Form.Field>
        </Flex>

        <Flex mt="5" gap="3" justify="end">
          <Dialog.Close>
            <Button
              variant="secondary"
              type="button"
              disabled={sendEmailChange.isPending}
            >
              <Translation
                defaultMessage="Cancel"
                id="hHNj31"
                description="Cancel button text"
              />
            </Button>
          </Dialog.Close>
          <Button
            type="submit"
            loading={sendEmailChange.isPending}
            disabled={!newEmail.trim()}
          >
            <Translation
              defaultMessage="Send verification code"
              id="1bX9l4"
              description="Button text to send the email change verification code"
            />
          </Button>
        </Flex>
      </Form.Root>
    </>
  );
}

interface VerifyEmailChangeFormProps {
  newEmail: string;
  onBack: () => void;
  onSuccess: () => void;
}

function VerifyEmailChangeForm({
  newEmail,
  onBack,
  onSuccess,
}: VerifyEmailChangeFormProps) {
  const client = useQueryClient();
  const { setElevatedAccess } = useElevatedAccessToken();
  const verifyEmailChange = useVerifyEmailChange({
    mutation: {
      onSuccess: async (response) => {
        setElevatedAccess({
          token: response.elevatedAccessToken,
          expiresAt: response.expiresAt,
        });
        client.setQueryData<Me>(getMeQueryKey(), (old) =>
          old ? { ...old, ...response.user } : response.user,
        );
        await client.invalidateQueries({ queryKey: getMeQueryKey() });
      },
    },
  });

  const isSubmitting = verifyEmailChange.isPending;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const code = formData.get("otp-code")?.toString() ?? "";
    if (!code) return;
    verifyEmailChange.mutate({ data: { code } });
  };

  return (
    <Form.Root onSubmit={handleSubmit}>
      <Dialog.Title mb="2">
        <Translation
          defaultMessage="Enter verification code"
          id="upvyOt"
          description="Dialog title for entering the email change verification code"
        />
      </Dialog.Title>

      <Dialog.Description>
        <Translation
          defaultMessage="A verification code was sent to {email}. Please enter it below."
          id="9OH/qs"
          description="Description explaining where the email change code was sent"
          values={{
            email: (
              <Text weight="bold" highContrast>
                {newEmail}
              </Text>
            ),
          }}
        />
      </Dialog.Description>

      <Flex direction="column" gap="2" mt="5" mx="auto" width="fit-content">
        <Otp.Root
          autoSubmit
          gap="2"
          justify="center"
          columns="repeat(6, 48px)"
          width="fit-content"
          rows="48px"
          name="otp-code"
          readOnly={isSubmitting}
        >
          <Otp.Input required autoFocus autoComplete="off" />
          <Otp.Input required />
          <Otp.Input required />
          <Otp.Input required />
          <Otp.Input required />
          <Otp.Input required />
        </Otp.Root>

        {verifyEmailChange.error ? (
          <Text color="red" size="2" as="p">
            {getMutationErrorMessage(verifyEmailChange.error)}
          </Text>
        ) : null}
      </Flex>

      <Flex mt="5" gap="3" justify="between" align="center">
        <Button
          variant="secondary"
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
        >
          <Translation
            defaultMessage="Use a different email"
            id="HVuco9"
            description="Button text to go back and change the new email address"
          />
        </Button>
        <Flex gap="3">
          <Dialog.Close>
            <Button variant="secondary" type="button" disabled={isSubmitting}>
              <Translation
                defaultMessage="Cancel"
                id="hHNj31"
                description="Cancel button text"
              />
            </Button>
          </Dialog.Close>
          <SaveButton
            type="submit"
            loading={isSubmitting}
            done={verifyEmailChange.isSuccess}
            onDone={onSuccess}
          >
            <Translation
              defaultMessage="Confirm"
              id="EmYENK"
              description="Confirm button text for verification"
            />
          </SaveButton>
        </Flex>
      </Flex>

      <VisuallyHidden asChild>
        <section aria-live="polite">
          {verifyEmailChange.error
            ? getMutationErrorMessage(verifyEmailChange.error)
            : null}
        </section>
      </VisuallyHidden>
    </Form.Root>
  );
}

interface SetPasswordStepProps {
  newEmail: string;
  onDone: () => void;
}

function SetPasswordStep({ newEmail, onDone }: SetPasswordStepProps) {
  return (
    <SetPasswordForm
      description={
        <Dialog.Description>
          <Translation
            defaultMessage="Your email has been successfully updated to {email}. You can also set a password now if you'd like to be able to sign in with email and password."
            id="U7k/vL"
            description="Description for the set-password step after a successful email change"
            values={{
              email: (
                <Text weight="bold" highContrast>
                  {newEmail}
                </Text>
              ),
            }}
          />
        </Dialog.Description>
      }
      onSuccess={onDone}
      cancel={
        <Button variant="secondary" type="button" onClick={onDone}>
          <Translation
            defaultMessage="Not now"
            id="/7/PVX"
            description="Button to dismiss the set-password step and close the dialog"
          />
        </Button>
      }
    />
  );
}

function getMutationErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }

  return (
    <Translation
      defaultMessage="Something went wrong. Please try again."
      id="Dn/G2O"
      description="Generic error message for email change failure"
    />
  );
}
